import './App.css';
import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useParams, useLocation, useNavigationType } from 'react-router-dom';
import styled from 'styled-components';
import './fonts/fonts.css'

import { addHafasAttributes, JourneyWithHafas, TaggedJourney } from './utils/mapJourneysData';
import { fetchJourneys, FetchJourneysParams } from './utils/fetchJourneys';
import { useLocalStorage, saveSearchToRecent } from './hooks/useLocalStorage';
import { useSearchState } from './hooks/useSearchState';

import HomeScreen from './components/HomeScreen/HomeScreen';
import JourneysScreen from './components/JourneysScreen/JourneysScreen';
import JourneyDetailsScreen from './components/JourneyDetailsScreen';
import TrainStatisticsScreen from './components/StatisticsPage/TrainStatisticsScreen';
import TransferStatisticsScreen from './components/StatisticsPage/TransferStatisticsScreen';
import FooterSection from './components/FooterSection/FooterSection';
import Header from './components/Header/Header';
import APIPage from './components/ContentPages/DataPage';
import PrivacyPolicyPage from './components/ContentPages/Privacy';
import TermsConditionsPage from './components/ContentPages/Terms';
import ImprintPage from './components/ContentPages/Imprint';
import ContactPage from './components/ContentPages/Contact';
import WrappedPage from './components/ContentPages/WrappedPage/WrappedPage';
import Wrapped2025Page from './components/ContentPages/Wrapped2025Page/Wrapped2025Page';
import HeatwavePage from './components/ContentPages/HeatwavePage/HeatwavePage';
import AppLandingPage from './components/ContentPages/AppLandingPage/AppLandingPage';
import NotFoundPage from './components/ContentPages/NotFound';
import CookieBanner from './components/CookieBanner';
import { TimeDisplayProvider } from './components/Elements/TimeComponents';
import { Message } from './components/Message';
import { decodeHafasReference, encodeHafasReference, stripRedundantAttributes } from './utils/hafas-reference';
import { compressToURL, decompressFromURL } from './utils/compress';
import { getApi } from './utils/api';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
`

type JourneyDetailLocationState = {
  from?: string;
  journey?: TaggedJourney;
};

/** Scroll to top on client-side route changes (SPA navigation). */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType !== 'REPLACE') {
      window.scrollTo(0, 0);
    }
  }, [pathname, navigationType]);
  return null;
};

const JourneyDetailWrapper: React.FC<{ journeys: TaggedJourney[] }> = ({ journeys }) => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const state = location.state as JourneyDetailLocationState | null;
  const [journey, setJourney] = useState<JourneyWithHafas | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadJourney = async () => {
      const fromState =
        id && state?.journey && state.journey.id === id ? state.journey : undefined;
      const fromList = !fromState && id ? journeys.find((j) => j.id === id) : undefined;
      
      let loadedJourney: JourneyWithHafas | undefined = fromState ?? fromList;

      if (loadedJourney === undefined && id) {
        const hafas = decompressFromURL(id);
        try {
          loadedJourney = addHafasAttributes(await getApi('get-trip-details', { hafas }));
        } catch (err) {
          console.error('Failed to fetch journey:', err);
        }
      }
      
      setJourney(loadedJourney);
      setLoading(false);
    };

    loadJourney();
  }, [id, journeys, state]);

  if (loading) {
    return <Message type="loading" message='' />;
  }

  if (!journey) {
    return <Message
      type="timeout"
      message=''
    />;
  }

  return <JourneyDetailsScreen journey={journey} />;
};


const App = () => {
  const [loading, setLoading] = useState(false);
  const [hasError, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>(''); 

  // Manage search state
  const {
    originId,
    setOrigin,
    destinationId,
    setDestination,
    originName,
    setOriginName,
    destinationName,
    setDestinationName,
    date,
    setDate,
    tripType,
    setTripType,
    options,
    setOptions,
  } = useSearchState();

  // Load data from localStorage
  const { 
    journeys, 
    setJourneys, 
    earlierThan, 
    setEarlierThan, 
    laterThan, 
    setLaterThan, 
    recentSearches, 
    setRecentSearches 
  } = useLocalStorage();

  const handleFetchJourneys = (type: 'earlier' | 'later' | 'initial') => {
    // Validate required parameters before fetching
    if (type === 'initial' && (!originId || !destinationId)) {
      console.warn('Cannot fetch journeys: missing origin or destination', { originId, destinationId });
      setError(true);
      setErrorMessage('no-journeys');
      setLoading(false);
      return;
    }
    
    // Clear the error state before starting a new search
    setError(false);

    if (type === 'initial') {
      // For initial searches, if no date is set, use current datetime
      const searchDate = date || new Date().toISOString();
      
      // Save the search parameters in localStorage
      saveSearchToRecent(originId, originName, destinationId, destinationName, searchDate, tripType, options, setRecentSearches);

      // Clear the journeys only for initial search
      setJourneys([]);
    }
    
    const args: FetchJourneysParams = {
      type,
      originId,
      destinationId,
      date: date || new Date().toISOString(),
      tripType,
      options,
      existingJourneys: type === 'initial' ? [] : journeys,
      onJourneysFetched: (fetchedJourneys, newEarlierThan, newLaterThan) => {
        // Check if journeys array is empty for initial searches
        if (type === 'initial' && fetchedJourneys.length === 0) {
          setError(true);
          setErrorMessage('no-journeys');
          setJourneys([]);
          return;
        }
        
        setError(false); // Clear error if we got journeys

        setJourneys(fetchedJourneys);

        // Update earlierThan or laterThan based on the type of fetch
        if (type === 'earlier' && newEarlierThan !== earlierThan) {
          setEarlierThan(newEarlierThan);
        } else if (type === 'later' && newLaterThan !== laterThan) {
          setLaterThan(newLaterThan);
        } else if (type !== 'earlier' && type !== 'later') {
          // For the initial or non-paginated fetch, update both earlierThan and laterThan
          setEarlierThan(newEarlierThan);
          setLaterThan(newLaterThan);
        }
      },
      onError: (message) => {
        setError(true);
        setErrorMessage(message);
      },
      onLoadingChange: setLoading,
      earlierThan: null,
      laterThan: null,
    };

    if (type === 'earlier') {
      args.earlierThan = earlierThan;
    } else if (type === 'later') {
      args.laterThan = laterThan;
    }

    fetchJourneys(args);
  };

  return (
    <Router>
    <ScrollToTop />
    <TimeDisplayProvider>
    <Container>
      <Header/>
      <Routes>
        <Route
          path="/"
          element={
            <HomeScreen
              setOrigin={setOrigin}
              setDestination={setDestination}
              setOriginName={setOriginName}  
              setDestinationName={setDestinationName}  
              setDate={setDate}
              setTripType={setTripType}
              setOptions={setOptions}
              handleFetchJourneys={handleFetchJourneys}
              screen="home"
              recentSearches={recentSearches}
              originId={originId}  
              destinationId={destinationId}  
              originName={originName}  
              destinationName={destinationName}  
              date={date}  
              tripType={tripType}  
              options={options}  
            />
          }
        />
        <Route
          path="/journeys"
          element={
            <JourneysScreen
              journeys={journeys}
              loading={loading}
              hasError={hasError}
              errorMessage={errorMessage}
              earlierThan={earlierThan}
              laterThan={laterThan}
              handleFetchJourneys={handleFetchJourneys}
              setOrigin={setOrigin}
              setDestination={setDestination}
              setOriginName={setOriginName}  
              setDestinationName={setDestinationName}  
              setDate={setDate}
              setTripType={setTripType}
              setOptions={setOptions}
              screen='journeys'
              recentSearches={recentSearches}
              originId={originId}  
              destinationId={destinationId}  
              originName={originName}  
              destinationName={destinationName}  
              date={date}  
              tripType={tripType}  
              options={options}  
            />
          }
        />

        <Route path="/journey/:id" element={<JourneyDetailWrapper journeys={journeys} />} />

        <Route path="/train-statistics" element={<TrainStatisticsScreen />} />

        <Route path="/transfer-statistics" element={<TransferStatisticsScreen />} />

        <Route path="/data" element={<APIPage />} />

        <Route path="/contact" element={<ContactPage />} />

        <Route path="/privacy" element={<PrivacyPolicyPage />} />

        <Route path="/terms" element={<TermsConditionsPage />} />

        <Route path="/imprint" element={<ImprintPage />} />

        <Route path="/2024wrapped" element={<WrappedPage />} />

        <Route path="/2025wrapped" element={<Wrapped2025Page />} />

        <Route path="/heatwave" element={<HeatwavePage />} />

        <Route path="/app" element={<AppLandingPage />} />

        {/* Catch-all route for 404 errors - must be last */}
        <Route path="*" element={<NotFoundPage />} />

        {/* IMPORTANT!
        These root paths are reserved and should NOT be used:
        - /api -> to serve the API server on the same domain if needed
        - /go -> to put the new webapp on
        A system upstream from this react app may at any point decide to route requests to these paths somewhere else.
        */}

      </Routes>
      <CookieBanner />
      <FooterSection />
    </Container>
    </TimeDisplayProvider>
    </Router>
  );
};

export default App;
